/**
 * Promotional Card Carousel
 *
 * Wires the optional proportional progress indicator and previous/next
 * controls to a native CSS scroll-snap track. No library dependency.
 * The track itself (<ul>) and slides (<li>) are styled entirely in CSS;
 * this module only observes scroll position and reacts to arrow clicks.
 */

class PromotionalCardCarousel {
    constructor(container) {
        this.container = container;
        this.track = container.querySelector('.promotional-carousel__track');
        this.progressThumb = container.querySelector('.promotional-carousel__progress-thumb');
        this.prevBtn = container.querySelector('.promotional-carousel__arrow--prev');
        this.nextBtn = container.querySelector('.promotional-carousel__arrow--next');
        this.slides = container.querySelectorAll('.promotional-carousel__slide');

        if (!this.track) return;

        this.resizeTimer = null;
        this.controller = new AbortController();
        this.init();
    }

    init() {
        const { signal } = this.controller;

        this.track.addEventListener('scroll', () => {
            this.updateProgress();
            this.updateArrowStates();
        }, { signal });

        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', () => this.scroll('prev'), { signal });
        }
        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', () => this.scroll('next'), { signal });
        }

        window.addEventListener('resize', () => {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => {
                this.updateProgress();
                this.updateArrowStates();
            }, 100);
        }, { signal });

        this.updateProgress();
        this.updateArrowStates();
    }

    /**
     * Position and size the progress thumb proportional to scroll position.
     * Thumb width reflects the visible-to-total ratio (floored at 10%).
     */
    updateProgress() {
        if (!this.progressThumb || !this.track) return;

        const scrollLeft = this.track.scrollLeft;
        const scrollWidth = this.track.scrollWidth;
        const clientWidth = this.track.clientWidth;
        const maxScroll = scrollWidth - clientWidth;

        if (maxScroll <= 0) {
            // All content visible — fill the track.
            this.progressThumb.style.width = '100%';
            this.progressThumb.style.left = '0';
            return;
        }

        const visibleRatio = clientWidth / scrollWidth;
        const thumbWidth = Math.max(visibleRatio * 100, 10);
        const scrollProgress = scrollLeft / maxScroll;
        const thumbLeft = scrollProgress * (100 - thumbWidth);

        this.progressThumb.style.width = `${thumbWidth}%`;
        this.progressThumb.style.left = `${thumbLeft}%`;
    }

    /**
     * Disable the previous arrow at the start edge and the next arrow at the
     * end edge, so the controls always reflect whether more content exists in
     * that direction.
     */
    updateArrowStates() {
        if (!this.track) return;

        const scrollLeft = this.track.scrollLeft;
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const threshold = 5;

        if (this.prevBtn) {
            this.prevBtn.disabled = scrollLeft <= threshold;
        }
        if (this.nextBtn) {
            this.nextBtn.disabled = scrollLeft >= maxScroll - threshold;
        }
    }

    /**
     * Advance the track by one slide (slide width + gap) in the given
     * direction. Falls back gracefully if there are no slides to measure.
     */
    scroll(direction) {
        if (!this.track || !this.slides.length) return;

        const slide = this.slides[0];
        const slideWidth = slide.offsetWidth;
        const gap = parseInt(window.getComputedStyle(this.track).gap, 10) || 16;
        const distance = (direction === 'next') ? (slideWidth + gap) : -(slideWidth + gap);

        this.track.scrollBy({ left: distance, behavior: 'smooth' });
    }
}

function initPromotionalCarousels() {
    document.querySelectorAll('.promotional-card-carousel').forEach((container) => {
        if (container.dataset.carouselInitialized) return;
        new PromotionalCardCarousel(container);
        container.dataset.carouselInitialized = 'true';
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPromotionalCarousels);
} else {
    initPromotionalCarousels();
}

// Re-initialise when the section re-loads in the Shopify theme editor.
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
        const section = event.target;
        if (section.querySelector('.promotional-card-carousel')) {
            initPromotionalCarousels();
        }
    });
}

window.PromotionalCardCarousel = PromotionalCardCarousel;
